import { Fragment, memo } from 'react';
import styled from '@emotion/styled';

import { H3 } from './PageItems/HeadlineItem';
import PhotoAlbum from './PageItems/PhotoAlbum';

import {
  isEventGroupItem,
  type Maybe,
  type Wws_Page,
  type Wws_PageItem,
  type Wws_PhotoAlbum,
} from '../types/graphql';
import InlineEditHandler from './InlineEdit/InlineEditHandler/InlineEditHandler';
import { EventGroupItemPageContent, PageContent } from './PageContent';
import { buildEditPageItemContextMenuButtons } from './InlineEdit/InlineEditMessageFactory';

export type ComponentOverrideMapValue = React.FC<{
  item: Wws_PageItem;
  className?: string;
}>;

export type ComponentOverrideMap = Map<string, ComponentOverrideMapValue>;

const WeddingPageContainer = styled('div')`
  max-width: 605px;
  margin: 0 auto;
`;

const Grid = styled('div')`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;

  // Handles the flex-basis in Edit Mode
  & > .InlineEditHandler {
    flex-basis: 100%;
  }

  & > .InlineEditHandler:has(> * > .PersonItem) {
    flex-basis: 50%;
  }

  // Handles the flex-basis in Normal Mode
  & > * {
    flex-basis: 100%;
  }

  & > .PersonItem {
    flex-basis: 50%;
  }
`;

export interface PageContentsProps {
  page: Wws_Page;
  photoAlbums: Maybe<ReadonlyArray<Wws_PhotoAlbum>> | undefined;
  componentOverrides?: ComponentOverrideMap;
}

function PageContents({
  page,
  photoAlbums,
  componentOverrides = new Map<string, ComponentOverrideMapValue>(),
}: PageContentsProps) {
  const isHomePage = page.routeName === '';
  const isRegistryPage = page.routeName === 'registry';

  const RootEl =
    page.type === 'WeddingPartyPage' ? WeddingPageContainer : Fragment;

  const GridEl = page.type === 'WeddingPartyPage' ? Grid : Fragment;

  if (page.items.length || (page.type === 'PhotoPage' && photoAlbums?.length)) {
    return (
      <Fragment>
        <RootEl>
          <GridEl>
            {page.type === 'PhotoPage' ? (
              <PhotoAlbum photoAlbums={photoAlbums} />
            ) : null}

            {page.items.map((item: Wws_PageItem) => {
              if (isEventGroupItem(item)) {
                return (
                  <EventGroupItemPageContent
                    key={item.id}
                    page={page}
                    item={item}
                    componentOverrides={componentOverrides}
                  />
                );
              }

              return (
                <InlineEditHandler
                  key={item.id}
                  contextMenuButtons={buildEditPageItemContextMenuButtons(
                    page,
                    item
                  )}
                >
                  <PageContent
                    key={item.id}
                    item={item}
                    componentOverrides={componentOverrides}
                  />
                </InlineEditHandler>
              );
            })}
          </GridEl>
        </RootEl>
      </Fragment>
    );
  }

  if (isHomePage || isRegistryPage) {
    return null;
  }

  return <H3>{page.title || ''} coming soon!</H3>;
}

export default memo(PageContents);
